import React, { useState, useEffect, useRef } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '../../urls';
import { TextField, List, ListItem, IconButton, Box, Typography, Button } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import uploadFileToBlob, { getPhotoUploadUrl } from '../../lib/azure-blob';
import { COLORS } from '@/theme/colors';
import { getUser } from '@/lib/common';
import { formatDate } from '@/lib/formatters';
import { T } from '@/theme/tokens';

// Brand-colour set; a name hashes deterministically to one so an avatar keeps
// the same colour across the whole conversation.
const AVATAR_COLORS = ['#1C71E3', '#7A4FD6', '#16A55C', '#E0863A', '#D6457A', '#0E8A8A'];
const hashColor = (name: string) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initials = (name: string) => {
  const p = (name || '').trim().split(/\s+/);
  return (((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase()) || '?';
};

const StyledList = styled(List)(({ theme }) => ({
  overflowY: 'auto',
  padding: '22px 24px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  background: '#FAFBFD',
}));

interface ChatBoxProps {
  DisputeNumber: string;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  fetchMessages: () => void;
}

export interface Message {
  id: string;
  message_body: string;
  id_user_created: number;
  username: string;
  date_created_formated: string;
  attachment_url: string | null;
  file_name: string | null;
  file_type: string | null;
}

interface PastedImage {
  file: File;
  previewUrl: string;
  name: string;
}

export default function ChatBox({
  DisputeNumber,
  fetchMessages,
  messages,
  setMessages,
}: Readonly<ChatBoxProps>) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messageValue, setMessageValue] = useState<string>('');
  const [fileSelected, setFileSelected] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [pastedImages, setPastedImages] = useState<PastedImage[]>([]);

  const [editingState, setEditingState] = useState<{
    id: string | null;
    text: string;
  }>({
    id: null,
    text: '',
  });

  const user = getUser();
  const userId = user?.id;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages?.length]);

  useEffect(() => {
    fetchMessages();
    const timer = setInterval(fetchMessages, 10000);

    return () => clearInterval(timer);
  }, [DisputeNumber, fetchMessages]);

  const generateUniqueFileName = (originalName: string): string => {
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop();
    return `${timestamp}-${randomString}.${extension}`;
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      const file = event.target.files[0];
      const maxSize = 10 * 1024 * 1024;

      if (file.size > maxSize) {
        setFileError('File size exceeds 10MB limit.');
        setFileSelected(null);
        return;
      }

      setFileError(null);
      setFileSelected(file);
    }
  };

  const handlePaste = async (event: React.ClipboardEvent<any>) => {
    const items = (
      event.clipboardData ?? (event as any).originalEvent.clipboardData
    ).items;
    let hasImage = false;
    const newPastedImages: PastedImage[] = [];

    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        hasImage = true;
        const file = item.getAsFile();
        if (file) {
          const maxSize = 10 * 1024 * 1024;
          if (file.size > maxSize) {
            setFileError('One or more pasted images exceed 10MB limit.');
            continue;
          }

          const previewUrl = URL.createObjectURL(file);
          newPastedImages.push({
            file,
            previewUrl,
            name: file.name,
          });
        }
      }
    }

    if (hasImage) {
      event.preventDefault();
      if (newPastedImages.length > 0) {
        setPastedImages((prev) => [...prev, ...newPastedImages]);
        if (!fileSelected) {
          setFileSelected(newPastedImages[0].file);
        }
      }
    }
  };

  const removePastedImage = (index: number) => {
    setPastedImages((prev) => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].previewUrl);
      newImages.splice(index, 1);
      if (newImages.length === 0) {
        setFileSelected(null);
        setFileError(null);
      }
      return newImages;
    });
  };

  const sendMessage = async () => {
    const messageContent = messageValue.trim();

    if (
      messageContent.length === 0 &&
      pastedImages.length === 0 &&
      !fileSelected
    ) {
      return;
    }

    setUploading(true);

    try {
      if (pastedImages.length > 0) {
        if (messageContent.length > 0) {
          await fetchWrapper.post(urls.DevOps.sendMessages, {
            disputeId: DisputeNumber,
            messageValue: messageContent,
            attachmentUrl: null,
            fileName: null,
            fileType: null,
          });
        }

        const imageUrls: string[] = [];
        const imageNames: string[] = [];
        const imageTypes: string[] = [];

        for (const image of pastedImages) {
          const uploaded = await uploadFileToBlob(image.file);
          const fileUrl = uploaded?.url
            ? await getPhotoUploadUrl(uploaded.url)
            : '';
          imageUrls.push(fileUrl);
          imageNames.push(image.name);
          imageTypes.push(image.file.type);
        }

        await fetchWrapper.post(urls.DevOps.sendMessages, {
          disputeId: DisputeNumber,
          messageValue: '',
          attachmentUrl: imageUrls.join('|'),
          fileName: imageNames.join('|'),
          fileType: imageTypes.join('|'),
        });
      } else if (fileSelected) {
        const uploaded = await uploadFileToBlob(fileSelected);
        const attachmentUrl = uploaded?.url
          ? await getPhotoUploadUrl(uploaded.url)
          : '';

        await fetchWrapper.post(urls.DevOps.sendMessages, {
          disputeId: DisputeNumber,
          messageValue: messageContent,
          attachmentUrl: attachmentUrl,
          fileName: fileSelected.name,
          fileType: fileSelected.type,
        });
      } else if (messageContent.length > 0) {
        await fetchWrapper.post(urls.DevOps.sendMessages, {
          disputeId: DisputeNumber,
          messageValue: messageContent,
          attachmentUrl: null,
          fileName: null,
          fileType: null,
        });
      }

      setMessageValue('');
      setFileSelected(null);
      setPastedImages([]);
      setFileError(null);

      await fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setUploading(false);
    }
  };

  const editMessage = (messageId: string, currentText: string) => {
    setEditingState({ id: messageId, text: currentText });
  };

  const saveEdit = (messageId: string) => {
    if (editingState.text.trim().length > 0) {
      fetchWrapper
        .post(urls.DevOps.updateMessage, {
          messageId: messageId,
          newMessage: editingState.text,
        })
        .then(async () => {
          setEditingState({ id: null, text: '' });
          await fetchMessages();
        })
        .catch((error: Error) => console.error('Error saving edit:', error));
    }
  };

  const cancelEdit = () => {
    setEditingState({ id: null, text: '' });
  };

  const deleteMessage = (messageId: string) => {
    const deletedBy = user?.username || 'Unknown User';
    const currentDate = new Date().toLocaleString();
    const deletedMessage = `[Message deleted by ${deletedBy} on ${currentDate}]`;

    fetchWrapper
      .post(urls.DevOps.updateMessage, {
        messageId: messageId,
        newMessage: deletedMessage,
      })
      .then(async () => {
        await fetchMessages();
      })
      .catch((error: Error) => console.error('Error deleting message:', error));
  };

  const renderAttachment = (message: Message) => {
    if (!message.attachment_url) return null;

    if (message.attachment_url.includes('|')) {
      const urls = message.attachment_url.split('|');
      const names = (message.file_name ?? '').split('|');
      const types = (message.file_type ?? '').split('|');

      return (
        <Box
          mt={1}
          sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {urls.map((url: string, index: number) => {
            const fileName = names[index] ?? 'Attachment';
            const fileType = types[index] ?? '';

            if (fileType.startsWith('image/')) {
              return (
                <Box key={`${url}-${index}`} sx={{ width: '100%' }}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-block', cursor: 'pointer' }}>
                    <img
                      src={url}
                      alt={fileName}
                      style={{
                        maxWidth: '300px',
                        maxHeight: '300px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    />
                  </a>
                </Box>
              );
            } else {
              return (
                <Box key={`${url}-${index}`} sx={{ width: '100%' }}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: COLORS.mutedBlue, textDecoration: 'none' }}>
                    <Typography variant="body2">📎 {fileName}</Typography>
                  </a>
                </Box>
              );
            }
          })}
        </Box>
      );
    }

    if (message.file_type?.startsWith('image/')) {
      return (
        <Box mt={1} sx={{ width: '100%' }}>
          <a
            href={message.attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', cursor: 'pointer' }}>
            <img
              src={message.attachment_url}
              alt={message.file_name ?? 'Attached Image'}
              style={{
                maxWidth: '300px',
                maxHeight: '300px',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
          </a>
        </Box>
      );
    } else {
      return (
        <Box mt={1} sx={{ width: '100%' }}>
          <a
            href={message.attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.mutedBlue, textDecoration: 'none' }}>
            <Typography variant="body2">
              📎 {message.file_name ?? 'Attached File'}
            </Typography>
          </a>
        </Box>
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
      }}>
      <StyledList sx={{ flex: 1, minHeight: 0, height: 'auto' }}>
        {messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: '200px',
              textAlign: 'center',
              px: 3,
            }}>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 400 }}>
              No messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start the conversation by sending a message below. You can paste
              images from clipboard and attach them to your message.
            </Typography>
          </Box>
        ) : (
          messages.map((message: Message, index: number) => {
            const isEditing = editingState.id === message.id;
            const isCurrentUser = Number(userId) === message.id_user_created;
            const hasAnyNotMineMessageAfterCurrent = messages
              .slice(index + 1)
              .some((msg) => msg.id_user_created !== message.id_user_created);
            const showControls =
              isCurrentUser && !hasAnyNotMineMessageAfterCurrent && !isEditing;

            return (
              <ListItem
                key={message.id}
                disableGutters
                sx={{
                  width: '100%',
                  px: 0,
                  py: 0,
                  display: 'flex',
                  flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  gap: 1.25,
                }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    mt: '2px',
                    flex: 'none',
                    borderRadius: '50%',
                    background: hashColor(message.username),
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12.5,
                    fontWeight: 800,
                  }}
                  title={message.username}>
                  {initials(message.username)}
                </Box>
                <Box
                  sx={{
                    maxWidth: 600,
                    width: isEditing ? '100%' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                  }}>
                  {/* Meta: author + time */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mb: 0.5,
                      px: 0.5,
                      flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                    }}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: '#5A6472',
                        lineHeight: 1.2,
                      }}>
                      {isCurrentUser ? 'You' : message.username}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 12,
                        color: '#9BA6B7',
                        lineHeight: 1.2,
                      }}>
                      {formatDate(message.date_created_formated, true)}
                    </Typography>
                  </Box>

                  {isEditing ? (
                    <Box sx={{ width: '100%' }}>
                      <TextField
                        fullWidth
                        value={editingState.text}
                        onChange={(e) =>
                          setEditingState((prev) => ({
                            ...prev,
                            text: e.target.value,
                          }))
                        }
                        multiline
                        rows={2}
                        variant="outlined"
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: `${T.radiusSm}px`,
                            backgroundColor: T.surface,
                          },
                        }}
                      />
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          gap: 1,
                          justifyContent: 'flex-end',
                        }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => saveEdit(message.id)}>
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<CancelIcon />}
                          onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        backgroundColor: isCurrentUser ? '#1C71E3' : '#F1F4F9',
                        color: isCurrentUser ? '#FFFFFF' : '#223',
                        borderRadius: '16px',
                        borderTopRightRadius: isCurrentUser ? '5px' : '16px',
                        borderTopLeftRadius: isCurrentUser ? '16px' : '5px',
                        px: '16px',
                        py: '12px',
                        maxWidth: '100%',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}>
                      <Typography
                        sx={{
                          fontSize: 14.5,
                          lineHeight: 1.55,
                          whiteSpace: 'pre-wrap',
                          color: 'inherit',
                        }}>
                        {message.message_body}
                      </Typography>
                      {renderAttachment(message)}
                    </Box>
                  )}

                  {showControls && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.25,
                        mt: 0.25,
                        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
                      }}>
                      <IconButton
                        size="small"
                        aria-label="Edit message"
                        onClick={() =>
                          editMessage(message.id, message.message_body)
                        }
                        sx={{ p: 0.25, color: T.textFaint3 }}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete message"
                        onClick={() => deleteMessage(message.id)}
                        sx={{
                          p: 0.25,
                          color: T.textFaint3,
                          '&:hover': { color: COLORS.danger },
                        }}>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </ListItem>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </StyledList>

      <Box
        sx={{
          flexShrink: 0,
          p: '14px 24px',
          borderTop: '1px solid #EEF1F6',
          background: '#fff',
        }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'flex-end',
          }}>
          <input
            id="chatbox-attachment-input"
            type="file"
            onChange={onFileChange}
            style={{ display: 'none' }}
          />
          <Box
            component="label"
            htmlFor="chatbox-attachment-input"
            aria-label="Attach file"
            sx={{
              width: 40,
              height: 40,
              flex: 'none',
              borderRadius: '10px',
              border: '1px solid #E1E7F0',
              color: '#5A6472',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { background: '#F2F7FD' },
            }}>
            <AttachFileIcon fontSize="small" />
          </Box>
          <TextField
            size="small"
            placeholder="Write a message…"
            value={messageValue}
            onChange={(e) => setMessageValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            onPaste={handlePaste}
            multiline
            minRows={1}
            sx={{
              flex: 1,
              minWidth: 120,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: '#fff',
                fontSize: 14.5,
                '& fieldset': { borderColor: '#D8E0EA' },
                '&:hover fieldset': { borderColor: '#C3D0E2' },
                '&.Mui-focused fieldset': {
                  borderColor: '#1C71E3',
                  boxShadow: '0 0 0 3px rgba(28,113,227,.12)',
                },
              },
              '& .MuiInputBase-input': {
                padding: '12px 16px',
                maxHeight: 96,
                overflowY: 'auto',
              },
            }}
            inputProps={{ 'aria-label': 'Message' }}
          />
          <Button
            onClick={sendMessage}
            disabled={uploading || !!fileError}
            variant="contained"
            endIcon={<SendIcon sx={{ fontSize: 16 }} />}
            sx={{
              height: 44,
              px: 2.25,
              flex: 'none',
              borderRadius: '10px',
              backgroundColor: '#1C71E3',
              fontSize: 14,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { backgroundColor: T.primaryHover, boxShadow: 'none' },
              '&.Mui-disabled': { backgroundColor: T.neutralGrey, color: '#fff' },
            }}>
            Send
          </Button>
        </Box>
        {pastedImages.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {pastedImages.map((image: PastedImage, index: number) => (
              <Box key={index} sx={{ position: 'relative' }}>
                <img
                  src={image.previewUrl}
                  alt={`Pasted ${index + 1}`}
                  style={{
                    maxWidth: '100px',
                    maxHeight: '100px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => removePastedImage(index)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'white',
                    '&:hover': { backgroundColor: 'white' },
                  }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        {fileError && (
          <Typography
            variant="caption"
            display="block"
            sx={{ color: T.danger, mt: 1 }}>
            {fileError}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
